import React, { useMemo, useState } from 'react';

import { Box, Button, Group, Text, Tooltip, createStyles } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangle, IconClipboardText, IconGift } from '@tabler/icons-react';
import KiloButton from 'components/KiloButton';
import Menu, { MenuItemProps } from 'components/Menu';
import ModalLateCancelNoShow from 'components/ModalLateCancelNoShow';
import dayjs from 'dayjs';
import useAppointment from 'hooks/appointment/useAppointment';
import useUpdateAthleteAppointment from 'hooks/athleteAppointment/useUpdateAthleteAppointment';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import ReservationStatusDrawer from 'pages/private/ownerMobile/Schedule/components/ReservationStatusDrawer';
import { useNavigate } from 'react-router-dom';
import { AthleteAppointmentRosterRow } from 'types';
import { LastCreditAlertContent } from 'utils/last-credit-alert';
import notificationHandler from 'utils/notifications';

import ModalAthleteAppointmentDelete from '../../ModalAthleteAppointmentDelete';

const useStyles = createStyles(theme => ({
  row: {
    display: 'grid',
    alignItems: 'center',
    padding: theme.spacing.xs,
    gridTemplateColumns: 'min-content auto auto',
    gridTemplateRows: 'auto',
    gap: theme.spacing.sm,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
}));

interface IReservedAndCheckedInAthleteProps {
  athleteAppointment: AthleteAppointmentRosterRow;
  index: number;
  handleCloseDrawer: () => void;
  athleteAppointmentsLength: number;
  permission?: boolean;
  setLastCreditAlert: React.Dispatch<React.SetStateAction<LastCreditAlertContent | null>>;
}

const ReservedAndCheckedInAthlete = ({
  athleteAppointment,
  index,
  handleCloseDrawer,
  athleteAppointmentsLength,
  permission = true,
  setLastCreditAlert,
}: IReservedAndCheckedInAthleteProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();
  const staffTracking = useStaffTracking();

  const [isLateCancel, setIsLateCancel] = useState<boolean>(true);
  const [openedTooltip, setOpenedTooltip] = useState(false);
  const [openedBirthdayTooltip, setOpenedBirthdayTooltip] = useState(false);
  const [openedNotesTooltip, setOpenedNotesTooltip] = useState(false);

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [statusModalOpened, { open: openStatusModal, close: closeStatusModal }] = useDisclosure(false);
  const [
    lateCancelNoShowModalOpened,
    { open: openLateCancelNoShowModal, close: closeLateCancelNoShowModal },
  ] = useDisclosure(false);

  const { data: appointmentData } = useAppointment(athleteAppointment.appointment);

  const updateAthleteAppointmentMutation = useUpdateAthleteAppointment();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('staff');

  const hasPastDueBills = athleteAppointment.hasPastDueBills;
  const hasUnsignedDocument = athleteAppointment.hasUnsignedDocuments;
  const isCredited = athleteAppointment.isCredited;
  const hasNotification = hasPastDueBills || hasUnsignedDocument || !isCredited;

  const isBirthday = athleteAppointment.athlete.dob
    ? dayjs(new Date(athleteAppointment.athlete.dob)).format('MM-DD') ===
      dayjs(new Date(appointmentData?.data.startDate ?? '')).format('MM-DD')
    : false;

  const getNotificationMessage = (): string => {
    let notification = '';
    if (hasPastDueBills) {
      notification += ' UNPAID INVOICE';
    }
    if (hasUnsignedDocument) {
      if (notification.length > 1) {
        notification += ' & ';
      }
      notification += ' UNSIGNED DOCUMENT';
    }
    if (!isCredited) {
      if (notification.length > 1) {
        notification += ' & ';
      }
      notification += 'UNCREDITED RESERVATION';
    }
    return `This profile has one or more issues that require attention: ${notification}.`;
  };

  const handleNameClick = (): void => {
    navigate(`/users/athletes/${athleteAppointment.athlete.athleteId}/general-info`);
  };

  const handleUpdate = (ap: AthleteAppointmentRosterRow): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const payload = {
      ...ap,
      isCheckedIn: !ap.isCheckedIn,
      athlete: ap.athlete.athleteId,
      chargeNow: null,
      chargeDate: null,
      chargeFee: null,
      taxRate: null,
      deductCredit: null,
    };
    updateAthleteAppointmentMutation.mutate(payload, {
      onSuccess: response => {
        successNotification(
          'Athlete reservation update success',
          `${ap.athlete.firstName} ${ap.athlete.lastName}'s reservation successfully updated.`,
        );
        if (appointmentData?.data?.appointmentId && payload.isCheckedIn) {
          staffTracking.trackAthleteCheckedIn(
            ap.athlete.athleteId,
            `${ap.athlete.firstName} ${ap.athlete.lastName}`,
            appointmentData?.data.appointmentId,
            appointmentData?.data.appointmentType?.name || '',
            'appointment',
          );
        }
        if (
          (payload.isCheckedIn || payload.isLateCancel || payload.isNoShow) &&
          response.data?.creditDispensation
        ) {
          getAthletePackageAllLimitedCreditsUsed.mutate(
            { creditDispensation: response.data.creditDispensation },
            {
              onSuccess: res => {
                const alert = buildLastCreditAlert(res.data, ap.athlete.firstName);
                if (alert) {
                  setLastCreditAlert(alert);
                }
              },
              onError: error => {
                errorNotification(
                  'Error to check if all limited credits used',
                  error.response?.data.message,
                );
              },
            },
          );
        }
      },
      onError: error => {
        errorNotification('Athlete reservation update failed', error.response?.data.message);
      },
    });
  };

  const handleLateCancel = (): void => {
    setIsLateCancel(true);
    openLateCancelNoShowModal();
  };

  const handleNoShow = (): void => {
    setIsLateCancel(false);
    openLateCancelNoShowModal();
  };

  const menuItems: MenuItemProps[] = useMemo(() => {
    let items: MenuItemProps[] = [
      {
        title: 'Remove Reservation',
        onClick: () => openDeleteModal(),
      },
    ];
    if (isCredited) {
      items.unshift(
        ...[
          {
            title: 'Mark Late Cancel',
            onClick: () => handleLateCancel(),
            disabled: athleteAppointment.isCheckedIn,
          },
          {
            title: 'Mark No Show',
            onClick: () => handleNoShow(),
            disabled: athleteAppointment.isCheckedIn,
          },
        ],
      );
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteAppointment.isCheckedIn, isCredited]);

  const athleteAppointmentReservationTitle = useMemo(() => {
    if (athleteAppointment.isCheckedIn) {
      return 'Checked In';
    }

    if (!athleteAppointment.isCheckedIn && athleteAppointment.isReserved) {
      if (athleteAppointment.isUncreditedReservation) {
        return 'Reserved (Uncredited)';
      }
      return 'Reserved';
    }

    return null;
  }, [
    athleteAppointment.isCheckedIn,
    athleteAppointment.isReserved,
    athleteAppointment.isUncreditedReservation,
  ]);

  return (
    <>
      <Box className={classes.row} bg={athleteAppointment.isCheckedIn ? theme.colors.green[1] : 'default'}>
        <Text c='dimmed' size='sm'>
          {index + 1}
        </Text>
        <Group noWrap position='left'>
          <Text
            weight='bold'
            underline
            sx={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => handleNameClick()}
          >
            {athleteAppointment.athlete.fullName}
          </Text>

          {hasNotification && isDesktop && (
            <Tooltip label={getNotificationMessage()} multiline>
              <IconAlertTriangle size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.red[6]} />
            </Tooltip>
          )}
          {athleteAppointment.athlete.notes?.length > 0 && isDesktop && (
            <Tooltip label='This profile has one or more notes to review.'>
              <IconClipboardText size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.blue[6]} />
            </Tooltip>
          )}
          {athleteAppointment.athlete.notes?.length > 0 && !isDesktop && (
            <Tooltip label='This profile has one or more notes to review.' opened={openedNotesTooltip}>
              <Button onClick={() => setOpenedNotesTooltip(o => !o)} variant='subtle' compact>
                <IconClipboardText size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.blue[6]} />
              </Button>
            </Tooltip>
          )}
          {isBirthday && isDesktop && (
            <Tooltip label='This athlete has a birthday today.'>
              <IconGift size={theme.fontSizes.xl} strokeWidth={2} color='green' />
            </Tooltip>
          )}
          {hasNotification && !isDesktop && (
            <Tooltip multiline label={getNotificationMessage()} opened={openedTooltip}>
              <Button onClick={() => setOpenedTooltip(o => !o)} variant='subtle' compact>
                <IconAlertTriangle size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.red[6]} />
              </Button>
            </Tooltip>
          )}
          {isBirthday && !isDesktop && (
            <Tooltip label='This athlete has a birthday today.' opened={openedBirthdayTooltip}>
              <Button onClick={() => setOpenedBirthdayTooltip(o => !o)} variant='subtle' compact>
                <IconGift size={theme.fontSizes.xl} strokeWidth={2} color='green' />
              </Button>
            </Tooltip>
          )}
        </Group>
        <Group style={{ justifyContent: 'end' }} noWrap spacing='xs'>
          <Text c='dimmed' size='sm'>
            {athleteAppointmentReservationTitle}
          </Text>

          <Menu
            items={menuItems}
            disabled={(athleteAppointment.isCheckedIn && !athleteAppointment.isReserved) || !permission}
            onOpen={!isDesktop ? openStatusModal : undefined}
          />
          <KiloButton
            variant={athleteAppointment.isCheckedIn ? 'outline' : 'filled'}
            onClick={() => handleUpdate(athleteAppointment)}
            h={28}
            px={7}
            loading={updateAthleteAppointmentMutation.isLoading}
          >
            <Text>{athleteAppointment.isCheckedIn ? 'Remove Check In' : 'Check In'}</Text>
          </KiloButton>
        </Group>
      </Box>
      <ModalAthleteAppointmentDelete
        opened={deleteModalOpened}
        handleClose={closeDeleteModal}
        athleteAppointmentId={athleteAppointment.athleteAppointmentId}
        handleCloseDrawer={handleCloseDrawer}
        athleteAppointmentsLength={athleteAppointmentsLength}
      />
      <ModalLateCancelNoShow
        opened={lateCancelNoShowModalOpened}
        isLateCancel={isLateCancel}
        handleClose={closeLateCancelNoShowModal}
        eventType='appointment'
        athleteEventId={athleteAppointment.athleteAppointmentId}
        athleteId={athleteAppointment.athlete.athleteId}
        athleteName={`${athleteAppointment.athlete.firstName} ${athleteAppointment.athlete.lastName}`}
        eventId={athleteAppointment.appointment}
        eventName={appointmentData?.data.appointmentType?.name ?? ''}
        gymId={appointmentData?.data.gym}
        cancellationSettings={appointmentData?.data.cancellationSettings}
        triggeredBy='calendar'
        setLastCreditAlert={setLastCreditAlert}
      />
      {!isDesktop && (
        <ReservationStatusDrawer
          opened={statusModalOpened}
          athleteAppointment={athleteAppointment}
          handleUpdate={() => {
            handleUpdate(athleteAppointment);
            closeStatusModal();
          }}
          handleLateCancel={handleLateCancel}
          handleNoShow={handleNoShow}
          closeDrawer={closeStatusModal}
        />
      )}
    </>
  );
};

export default ReservedAndCheckedInAthlete;
